import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {FaEnvelope, FaInstagram, FaTimes, FaMapMarkerAlt, FaSubway, FaCar} from 'react-icons/fa';
import emailjs from 'emailjs-com';

const ContactContainer = styled.div`
    padding: 2rem 10rem;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        padding: 2rem 0;
    }
`;

const ContactList = styled.ul`
    list-style: none;
    padding: 0;
    margin-top: 1em;
`;

const ContactItem = styled.li`
    font-size: 1.2rem;
    margin-bottom: 2rem;
`;

const ContactSubItem = styled.div`
    font-size: 1rem;
    margin: 0.5rem 0;
    display: flex;
    align-items: flex-start;
`;

const Icon = styled.span`
    margin-right: 0.5rem;
    color: #4CAF50;
`;

const LocationInfo = styled.div`
    margin-left: 1.5rem;
`;

const ParkingInfo = styled.div`
    margin-top: 1rem;
    font-style: italic;
    color: #666;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 80%;
`;

const Input = styled.input`
    padding: 0.5rem;
    font-size: 1rem;
`;

const TextArea = styled.textarea`
    padding: 0.5rem;
    font-size: 1rem;
    height: 150px;
`;

const SubmitButton = styled.button`
    padding: 0.5rem 1rem;
    font-size: 1rem;
    background-color: #4CAF50;
    color: white;
    border: none;
    cursor: pointer;
`;

const MapContainer = styled.div`
    height: 400px;
    width: 100%;
    margin: 1.5rem 0;
`;

const IconLink = styled.a`
    display: inline-flex;
    align-items: center;
    margin-left: 0.5rem;
    color: #333;
    text-decoration: none;

    &:hover {
        color: #4CAF50;
    }
`;

const PopupContainer = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    padding: 20px;
    border-radius: 5px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
    position: absolute;
    top: 5px;
    right: 5px;
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
`;

const Contact: React.FC = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=jcgj632ipw';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
            if (mapRef.current && (window as any).naver && (window as any).naver.maps) {
                const location = new (window as any).naver.maps.LatLng(37.447080, 127.056809);
                const mapOptions = {
                    center: location,
                    zoom: 15,
                };
                const map = new (window as any).naver.maps.Map(mapRef.current, mapOptions);

                const locations = [
                    {
                        position: new (window as any).naver.maps.LatLng(37.447080, 127.056809),
                        title: 'Mayfleur',
                        content: '<div style="padding:5px;">Mayfleur</div>',
                        color: '#4f7bff'
                    },
                    {
                        position: new (window as any).naver.maps.LatLng(37.449730, 127.053897),
                        title: '내곡동 공영주차장',
                        content: '<div style="padding:5px;">내곡동 공영주차장</div>',
                        color: '#9e9d9d'
                    },
                    {
                        position: new (window as any).naver.maps.LatLng(37.445820, 127.056778),
                        title: '청계산근린광장공영주차장',
                        content: '<div style="padding:5px;">청계산근린광장공영주차장</div>',
                        color: '#9e9d9d'
                    },
                ];

                locations.forEach((loc) => {
                    const marker = new (window as any).naver.maps.Marker({
                        position: loc.position,
                        map: map,
                        title: loc.title,
                        icon: {
                            content: `
                <svg width="25" height="35" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 0C6.716 0 0 6.716 0 15c0 8.284 15 25 15 25s15-16.716 15-25c0-8.284-6.716-15-15-15z" fill="${loc.color}"/>
                  <circle cx="15" cy="15" r="7" fill="white"/>
                </svg>
              `,
                            anchor: new (window as any).naver.maps.Point(15, 40),
                        }
                    });

                    const infoWindow = new (window as any).naver.maps.InfoWindow({
                        content: loc.content,
                    });

                    (window as any).naver.maps.Event.addListener(marker, 'click', () => {
                        if (infoWindow.getMap()) {
                            infoWindow.close();
                        } else {
                            infoWindow.open(map, marker);
                        }
                    });
                });
            }
        };

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showPopup) {
            timer = setTimeout(() => {
                setShowPopup(false);
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [showPopup]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;

        if (!emailInput.value || !isValidEmail(emailInput.value)) {
            setPopupMessage('올바른 이메일 주소를 입력해주세요.');
            setShowPopup(true);
            return;
        }

        emailjs.sendForm('service_503rlpn', 'template_5q0mau9', form, '8k5--MtKQciw7gyeC')
            .then(() => {
                setPopupMessage('메시지가 성공적으로 전송되었습니다.');
                setShowPopup(true);
                form.reset();
            }, () => {
                setPopupMessage('메시지 전송에 실패했습니다. 다시 시도해주세요.');
                setShowPopup(true);
            });
    };

    const isValidEmail = (email: string) => {
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(email);
    };

    const closePopup = () => {
        setShowPopup(false);
    };

    return (
        <ContactContainer>
            <h2>Contact</h2>
            <ContactList>
                <ContactItem>
                    <h4>작업실 위치 안내</h4>
                    <ContactSubItem>
                        <Icon><FaMapMarkerAlt/></Icon>
                        <LocationInfo>
                            <strong>주소:</strong> 서울시 서초구 청계산로 207(신원동 659), 1층 109호
                        </LocationInfo>
                    </ContactSubItem>
                    <ContactSubItem>
                        <Icon><FaSubway/></Icon>
                        <LocationInfo>
                            <strong>대중교통:</strong> 지하철 청계산입구역 2번 출구에서 도보 3~5분 거리
                        </LocationInfo>
                    </ContactSubItem>
                    <ContactSubItem>
                        <Icon><FaCar/></Icon>
                        <LocationInfo>
                            <strong>차량이동:</strong> 작업실 근처 공영주차장을 이용해 주시기 바랍니다.
                            <ul>
                                <li>청계산근린광장 공영주차장 (도보 2분)</li>
                                <li>내곡동 공영주차장 (도보 7분)</li>
                                <li>청계산청룡 공영주차장 (도보 13분)</li>
                            </ul>
                            <ParkingInfo>
                                주의사항: 주말에는 등산객으로 인해 공영주차장이 혼잡할 수 있습니다.
                            </ParkingInfo>
                        </LocationInfo>
                    </ContactSubItem>
                    <MapContainer ref={mapRef}/>
                </ContactItem>
                <ContactItem>
                    <h4>이메일, 인스타그램, 카카오톡채널 안내</h4>
                    <ContactSubItem>
                        E-mail: sasa0713@naver.com
                        <IconLink href="mailto:sasa0713@naver.com">
                            <FaEnvelope/>
                        </IconLink>
                    </ContactSubItem>
                    <ContactSubItem>
                        Instagram: <a href="https://www.instagram.com/may.fleur" target="_blank"
                                      rel="noopener noreferrer">@may.fleur</a>
                        <IconLink href="https://www.instagram.com/may.fleur" target="_blank" rel="noopener noreferrer">
                            <FaInstagram/>
                        </IconLink>
                    </ContactSubItem>
                    <ContactSubItem>Kakaotalk: 메이플레르, mayfleur</ContactSubItem>
                </ContactItem>
                <ContactItem>
                    <h4>1:1 Q&A</h4>
                    <Form onSubmit={handleSubmit}>
                        <Input type="text" name="from_name" placeholder="Name" required/>
                        <Input type="tel" name="phone" placeholder="Phone Number" required/>
                        <Input type="email" name="email" placeholder="E-mail" required/>
                        <Input type="text" name="subject" placeholder="Subject" required/>
                        <TextArea name="message" placeholder="Message" required/>
                        <SubmitButton type="submit">Send</SubmitButton>
                    </Form>
                </ContactItem>
            </ContactList>
            {showPopup && (
                <PopupContainer>
                    <CloseButton onClick={closePopup}><FaTimes/></CloseButton>
                    <p>{popupMessage}</p>
                </PopupContainer>
            )}
        </ContactContainer>
    );
};

export default Contact;